import { api, API_ENDPOINTS } from './api';

// Types matching the backend DTOs
export interface Kid {
  id: number;
  name: string;
  age: number | null;
  grade: string;
  schoolName?: string;
  avatar?: string;
  overallProgress: number;
  completedCourses: number;
  activeCourses: number;
  lastActivity: string;
  currentCourse: string;
}

export interface Activity {
  id: number;
  childId: number;
  childName: string;
  type: 'course_completed' | 'quiz_passed' | 'badge_earned' | 'lesson_started' | 'assignment_submitted';
  title: string;
  description: string;
  timestamp: string;
  score?: number;
  courseName?: string;
}

export interface MessageThread {
  id: number;
  teacherName: string;
  subject: string;
  lastMessage: string;
  unreadCount: number;
  lastActivity: string;
  priority: 'normal' | 'high' | 'urgent';
}

export interface QuickMessage {
  teacherId: number;
  teacherName: string;
  subject: string;
  childName: string;
}

export interface DashboardStats {
  activeLearners: number;
  recentActivities: number;
  unreadMessages: number;
  weeklyAchievements: number;
}

export interface PendingKid {
  id: number;
  name: string;
  avatar?: string;
}

export interface ParentDashboardData {
  parentName: string;
  children: Kid[];
  pendingChildren: PendingKid[];
  recentActivities: Activity[];
  messageThreads: MessageThread[];
  quickMessages: QuickMessage[];
  stats: DashboardStats;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
  timestamp: string;
  errorCode?: string;
}

class ParentDashboardService {
  /**
   * Get parent dashboard data for the current user
   */
  async getParentDashboard(): Promise<ParentDashboardData> {
    try {
      const response = await api.get<ApiResponse<ParentDashboardData>>(
        API_ENDPOINTS.PARENT_DASHBOARD.DASHBOARD
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch parent dashboard');
      }
    } catch (error: any) {
      console.error('Error fetching parent dashboard:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch parent dashboard'
      );
    }
  }

  /**
   * Get parent dashboard data by parent ID
   */
  async getParentDashboardById(parentId: number): Promise<ParentDashboardData> {
    try {
      const response = await api.get<ApiResponse<ParentDashboardData>>(
        `${API_ENDPOINTS.PARENT_DASHBOARD.DASHBOARD_BY_ID}/${parentId}`
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch parent dashboard');
      }
    } catch (error: any) {
      console.error('Error fetching parent dashboard by ID:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch parent dashboard'
      );
    }
  }
}

export const parentDashboardService = new ParentDashboardService();
export default parentDashboardService;
